import React from 'react';
import useWindowWidth from '../hooks/useWindowWidth';
import Logo from '../components/Logo';
import { JournalIcon, SparkleIcon, ProfileIcon, ExportIcon } from '../components/Icons';

const APP_URL = 'https://app.bragsheet.io';

const PRIMARY = '#01696F';

export default function LandingPage() {
  const { isMobile } = useWindowWidth();

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#ffffff' }}>
      {/* Header */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: isMobile ? '1rem 1.25rem' : '1rem 3rem',
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <Logo size="small" />
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <a href={`${APP_URL}/pricing`} style={{
            fontSize: '0.875rem', fontWeight: '600',
            color: PRIMARY, textDecoration: 'none',
          }}>
            Pricing
          </a>
          <a href={`${APP_URL}/login`} style={{
            padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.875rem', fontWeight: '600',
            color: PRIMARY, textDecoration: 'none', border: `1px solid ${PRIMARY}`,
          }}>
            Sign In
          </a>
          <a href={`${APP_URL}/register`} style={{
            padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.875rem', fontWeight: '600',
            color: '#ffffff', backgroundColor: PRIMARY, textDecoration: 'none',
          }}>
            Get Started
          </a>
        </div>
      </header>

      {/* Hero */}
      <section style={{
        background: `linear-gradient(135deg, ${PRIMARY} 0%, #014f54 100%)`,
        color: '#ffffff',
        padding: isMobile ? '4rem 1.5rem' : '6rem 3rem',
        textAlign: 'center',
      }}>
        <h1 style={{
          fontSize: isMobile ? '2rem' : '3rem',
          fontWeight: '800',
          lineHeight: '1.15',
          marginBottom: '1.25rem',
          maxWidth: '720px',
          margin: '0 auto 1.25rem',
        }}>
          {/* T37: accent color #ccfbf1 on bg #01696F ≈ 5.15:1 — passes WCAG AA (was #5eead4 ≈ 4.39:1 — failed) */}
          Your college journey,{' '}
          <span style={{ color: '#ccfbf1' }}>documented.</span>
        </h1>
        {/* T38: subtitle on dark teal hero. rgba(255,255,255,0.85) blended onto
            #01696F yields ≈5.14:1 (passes AA, borderline). Bumped to 0.92 for
            a safer ≈6.0:1 ratio across browser color blending differences. */}
        <p style={{
          fontSize: isMobile ? '1rem' : '1.2rem',
          color: 'rgba(255,255,255,0.92)',
          maxWidth: '560px',
          margin: '0 auto 2.5rem',
          lineHeight: '1.6',
        }}>
          BragSheet helps high school students capture achievements, reflect on experiences,
          and build stronger college applications — powered by AI.
        </p>
        {/* T06: app-domain unauthenticated users intend to sign in — Sign In is primary, Start Free secondary (inverse of marketing T05) */}
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href={`${APP_URL}/login`} style={{
            padding: '0.85rem 2rem', borderRadius: '8px', fontSize: '1rem', fontWeight: '700',
            backgroundColor: '#ffffff', color: PRIMARY, textDecoration: 'none',
            minHeight: '44px', display: 'inline-flex', alignItems: 'center',
          }}>
            Sign In
          </a>
          <a href={`${APP_URL}/register`} style={{
            padding: '0.85rem 2rem', borderRadius: '8px', fontSize: '1rem', fontWeight: '600',
            backgroundColor: 'transparent', color: '#ffffff', textDecoration: 'none',
            border: '1px solid rgba(255,255,255,0.6)',
            minHeight: '44px', display: 'inline-flex', alignItems: 'center',
          }}>
            Start Free
          </a>
        </div>
        {/* T04: Replaces the previous decorative floating blobs (which read as
            rendering bugs) with a small angled "fake screenshot" of a sample
            journal entry. Show, don't tell. */}
        <div style={{
          marginTop: '3.5rem',
          display: 'flex',
          justifyContent: 'center',
          perspective: '1000px',
        }}>
          <div
            aria-hidden="true"
            style={{
              backgroundColor: '#ffffff',
              color: '#1e293b',
              borderRadius: '14px',
              padding: '1.25rem 1.5rem',
              maxWidth: '380px',
              width: '100%',
              textAlign: 'left',
              boxShadow: '0 18px 40px rgba(0, 0, 0, 0.25), 0 4px 12px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(255,255,255,0.4)',
              borderLeft: `4px solid ${PRIMARY}`,
              transform: isMobile ? 'rotate(-2deg)' : 'rotate(-3deg)',
            }}
          >
            <div style={{
              fontWeight: '700',
              fontSize: '0.95rem',
              marginBottom: '0.4rem',
              color: '#0f172a',
            }}>
              <span role="img" aria-label="journal entry">{'📝'}</span>{' '}Won regional debate finals
            </div>
            <div style={{
              fontSize: '0.825rem',
              color: '#475569',
              lineHeight: '1.5',
              marginBottom: '0.75rem',
            }}>
              I was up against a team that prepped harder than anyone — staying calm under cross-examination changed everything.
            </div>
            <span style={{
              display: 'inline-block',
              padding: '0.2rem 0.6rem',
              borderRadius: '999px',
              backgroundColor: '#f0fdfa',
              color: PRIMARY,
              fontSize: '0.7rem',
              fontWeight: '700',
              border: `1px solid ${PRIMARY}33`,
            }}>
              Leadership
            </span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={{
        padding: isMobile ? '3.5rem 1.5rem' : '5rem 3rem',
        backgroundColor: '#f8fafc',
      }}>
        <h2 style={{
          textAlign: 'center', fontSize: isMobile ? '1.5rem' : '2rem',
          fontWeight: '700', color: '#1e293b', marginBottom: '0.75rem',
        }}>
          Everything you need for college prep
        </h2>
        <p style={{
          textAlign: 'center', color: '#64748b', fontSize: '1rem',
          maxWidth: '500px', margin: '0 auto 3rem',
        }}>
          One place to track activities, reflect on growth, and prepare for applications.
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
          gap: '1.5rem',
          maxWidth: '1000px',
          margin: '0 auto',
        }}>
          {[
            { icon: <JournalIcon size={36} color="#3b82f6" />, bg: '#eff6ff', accent: '#3b82f6', title: 'Smart Journal', desc: 'Capture achievements, reflections, and milestones with categories and tags.' },
            { icon: <SparkleIcon size={36} color="#eab308" />, bg: '#fffbeb', accent: '#eab308', title: 'AI Insights', desc: 'Get auto-tags, essay theme suggestions, and activity descriptions powered by AI.' },
            { icon: <ProfileIcon size={36} color="#8b5cf6" />, bg: '#f5f3ff', accent: '#8b5cf6', title: 'Profile Builder', desc: 'Track GPA, test scores, activities, and awards — all in one place.' },
            { icon: <ExportIcon size={36} color="#22c55e" />, bg: '#f0fdf4', accent: '#22c55e', title: 'Easy Export', desc: 'Download your journal and profile as JSON or CSV for any application.' },
          ].map((f, i) => (
            <div key={i} style={{
              backgroundColor: '#ffffff', borderRadius: '16px', padding: '1.75rem',
              border: '1px solid #e2e8f0', textAlign: 'center',
              borderTop: `4px solid ${f.accent}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            }}>
              <div style={{
                width: '60px', height: '60px', borderRadius: '50%',
                backgroundColor: f.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 0.75rem',
              }}>{f.icon}</div>
              <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b', marginBottom: '0.5rem' }}>
                {f.title}
              </h3>
              <p style={{ fontSize: '0.875rem', color: '#64748b', lineHeight: '1.5', margin: 0 }}>
                {f.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section style={{
        padding: isMobile ? '3.5rem 1.5rem' : '5rem 3rem',
        background: 'linear-gradient(180deg, #ffffff 0%, #f0fdfa 100%)',
      }}>
        <h2 style={{
          textAlign: 'center', fontSize: isMobile ? '1.5rem' : '2rem',
          fontWeight: '700', color: '#1e293b', marginBottom: '3rem',
        }}>
          How it works
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: '2rem',
          maxWidth: '800px',
          margin: '0 auto',
        }}>
          {[
            { step: '1', color: '#3b82f6', title: 'Sign up in seconds', desc: 'Create your free account with email or Google. No credit card needed.' },
            { step: '2', color: '#01696F', title: 'Journal your journey', desc: 'Log activities, awards, and reflections. Use voice input or type — your choice.' },
            { step: '3', color: '#8b5cf6', title: 'Build your application', desc: 'AI helps you find essay themes, generate descriptions, and export everything.' },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{
                width: '48px', height: '48px', borderRadius: '50%',
                backgroundColor: s.color, color: '#ffffff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.25rem', fontWeight: '700',
                margin: '0 auto 1rem',
              }}>
                {s.step}
              </div>
              <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b', marginBottom: '0.5rem' }}>
                {s.title}
              </h3>
              <p style={{ fontSize: '0.875rem', color: '#64748b', lineHeight: '1.5', margin: 0 }}>
                {s.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{
        padding: isMobile ? '3.5rem 1.5rem' : '4.5rem 3rem',
        backgroundColor: PRIMARY,
        textAlign: 'center',
      }}>
        <h2 style={{
          fontSize: isMobile ? '1.5rem' : '2rem', fontWeight: '700',
          color: '#ffffff', marginBottom: '0.75rem',
        }}>
          Ready to start your college journey?
        </h2>
        {/* T38: CTA section paragraph on #01696F. Previously rgba(255,255,255,0.8)
            ≈4.75:1 (passes AA but tight). Bumped to 0.92 ≈6.0:1 for a safer margin. */}
        <p style={{
          color: 'rgba(255,255,255,0.92)', fontSize: '1rem',
          marginBottom: '2rem', maxWidth: '460px', margin: '0 auto 2rem',
        }}>
          Join students who are building stronger applications, one journal entry at a time.
        </p>
        <a href={`${APP_URL}/register`} style={{
          padding: '0.85rem 2.5rem', borderRadius: '8px', fontSize: '1rem', fontWeight: '700',
          backgroundColor: '#ffffff', color: PRIMARY, textDecoration: 'none',
          display: 'inline-flex', alignItems: 'center', minHeight: '44px',
        }}>
          Create Free Account
        </a>
      </section>

      {/* Footer */}
      {/* T40: footer links were #94a3b8 on #1e293b ≈5.27:1 (passes AA but
          tight for 12.8px small text). Copyright was #94a3b8 inherited.
          The previous #64748b copyright variant ≈3.14:1 fails AA. Bumped
          link + body color to #cbd5e1 ≈9.5:1 — comfortable AAA-level pass. */}
      <footer style={{
        padding: '1.5rem 3rem',
        backgroundColor: '#1e293b',
        textAlign: 'center',
        color: '#cbd5e1',
        fontSize: '0.8rem',
      }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <a href={`${APP_URL}/about`} style={{ color: '#cbd5e1', textDecoration: 'none', marginRight: '1.5rem' }}>About</a>
          <a href={`${APP_URL}/contact`} style={{ color: '#cbd5e1', textDecoration: 'none', marginRight: '1.5rem' }}>Contact</a>
          <a href={`${APP_URL}/faq`} style={{ color: '#cbd5e1', textDecoration: 'none', marginRight: '1.5rem' }}>FAQ</a>
          <a href={`${APP_URL}/pricing`} style={{ color: '#cbd5e1', textDecoration: 'none', marginRight: '1.5rem' }}>Pricing</a>
          <a href={`${APP_URL}/privacy`} style={{ color: '#cbd5e1', textDecoration: 'none', marginRight: '1.5rem' }}>Privacy Policy</a>
          <a href={`${APP_URL}/terms`} style={{ color: '#cbd5e1', textDecoration: 'none' }}>Terms of Service</a>
        </div>
        BragSheet &copy; {new Date().getFullYear()}
      </footer>
    </div>
  );
}
